import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import {
  Camera,
  Upload,
  Calendar,
  Users,
  DollarSign,
  TrendingUp,
  Eye,
  Trash2,
  Plus,
  BarChart3,
  Image,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  MapPin,
  User,
  Search,
  Filter,
  ShoppingCart,
  MessageCircle,
  Edit,
  CreditCard
} from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { getApiBaseUrl } from '@/config/production';
import { apiClient } from '@/services/api-client';
import { BankInfo } from '@/types/photographer';
import { RecentSales } from '@/components/dashboard/RecentSales';
import { MonthlySalesChart } from '@/components/dashboard/MonthlySalesChart';

interface PhotographerStats {
  totalPhotos: number;
  totalEvents: number;
  totalSales: number;
  totalRevenue: number;
  pendingApprovals: number;
  activeEvents: number;
  pendingSalesAmount?: number;
  pendingSalesCount?: number;
}

interface PhotoUpload {
  id: string;
  filename: string;
  original_filename?: string;
  storageurl?: string;
  thumbnailurl?: string;
  eventId?: string;
  eventName: string;
  uploadDate: string;
  status: 'processing' | 'approved' | 'rejected';
  sales: number;
  revenue: number;
}

// Interface específica para dashboard do fotógrafo
interface EventForDashboard {
  id: string;
  title: string;
  name?: string;
  date: string;
  status: string;
  photosCount?: number;
  createdAt: string;
  updatedAt: string;
  monetizationType: string;
  platformCommissionRate: number;
  photographerCommissionRate: number;
}

interface IdentifiedAthlete {
  id: string;
  name: string;
  bibNumber: string;
  photosCount: number;
  purchasesCount: number;
  totalSpent: number;
}

export default function PhotographerDashboard() {
  const [, setLocation] = useLocation();
  const { user, logout } = useUser();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<PhotographerStats>({
    totalPhotos: 0,
    totalEvents: 0,
    totalSales: 0,
    totalRevenue: 0,
    pendingApprovals: 0,
    activeEvents: 0
  });
  const [recentPhotos, setRecentPhotos] = useState<PhotoUpload[]>([]);
  const [events, setEvents] = useState<EventForDashboard[]>([]);
  const [identifiedAthletes, setIdentifiedAthletes] = useState<IdentifiedAthlete[]>([]);
  const [bankInfo, setBankInfo] = useState<BankInfo | null>(null);
  const [dashboardStats, setDashboardStats] = useState<PhotographerStats | null>(null);
  const [monthlySales, setMonthlySales] = useState<Array<{ name: string; total: number }>>([]);
  const [loadingMonthlySales, setLoadingMonthlySales] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoUpload | null>(null);
  const [photoToDelete, setPhotoToDelete] = useState<PhotoUpload | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Debug: verificar mudanças de estado
  useEffect(() => {
    if (selectedPhoto) {
      console.log('selectedPhoto mudou:', selectedPhoto);
    }
  }, [selectedPhoto]);

  useEffect(() => {
    if (photoToDelete) {
      console.log('photoToDelete mudou:', photoToDelete);
    }
  }, [photoToDelete]);

  // Verificar se o usuário é fotógrafo
  useEffect(() => {
    if (!user) {
      setLocation('/login');
      return;
    }

    if (user.role !== 'photographer') {
      setLocation('/');
      return;
    }

    loadDashboardData();
    loadBankInfo();
    loadDashboardStats();
  }, [user, setLocation]);

  // Usar dados reais do backend, com valores padrão apenas durante loading
  const statsData = {
    totalPhotos: dashboardStats?.totalPhotos ?? 0,
    totalSales: dashboardStats?.totalSales ?? 0,
    totalRevenue: dashboardStats?.totalRevenue ?? 0,
    pendingSalesAmount: dashboardStats?.pendingSalesAmount ?? 0,
    pendingSalesCount: dashboardStats?.pendingSalesCount ?? 0,
  };


  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-primary">Aprovada</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pendente</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejeitada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      if (!user?.id) {
        console.warn('Usuario nao disponivel para carregar dados do dashboard');
        return;
      }

      // Buscar fotos recentes para a aba "Visão Geral"
      // Limite fixo de 10 itens conforme especificação do dashboard
      try {
        const photosResponse = await apiClient.get(`/photographer/dashboard/recent-photos?limit=10`);
        if (photosResponse.data?.success && photosResponse.data?.data) {
          setRecentPhotos(photosResponse.data.data);
        }
      } catch (error) {
        console.error('Erro ao carregar fotos recentes:', error);
      }

      // Buscar eventos ativos para a aba "Visão Geral"
      // Limite fixo de 10 itens conforme especificação do dashboard
      try {
        const eventsResponse = await apiClient.get(`/photographer/dashboard/active-events?limit=10`);
        if (eventsResponse.data?.success && eventsResponse.data?.data) {
          setEvents(eventsResponse.data.data);
        }
      } catch (error) {
        console.error('Erro ao carregar eventos ativos:', error);
      }

      // Buscar vendas mensais
      try {
        setLoadingMonthlySales(true);
        const salesResponse = await apiClient.get('/photographer/dashboard/monthly-sales?months=6');
        if (salesResponse.data?.success && salesResponse.data?.data) {
          setMonthlySales(salesResponse.data.data);
        }
      } catch (error) {
        console.error('Erro ao carregar vendas mensais:', error);
      } finally {
        setLoadingMonthlySales(false);
      }

    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBankInfo = async () => {
    try {
      const { data } = await apiClient.get('/photographer/bank-info');
      if (data?.success && data?.data) {
        setBankInfo(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados bancários:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const { data } = await apiClient.get('/photographer/dashboard/stats');
      if (data?.success && data?.data) {
        setDashboardStats(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas do dashboard:', error);
    }
  };

  const getStatusBadgeDetailed = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge variant="default" className="bg-primary"><CheckCircle className="w-3 h-3 mr-1" />Aprovado</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rejeitado</Badge>;
      case 'processing':
        return <Badge variant="outline"><AlertCircle className="w-3 h-3 mr-1" />Processando</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Extrair apenas o primeiro nome do usuário
  const getFirstName = (name?: string | null) => {
    if (!name) return null;
    return name.split(' ')[0];
  };

  // Handler para visualizar foto
  const handleViewPhoto = (photo: PhotoUpload) => {
    console.log('Visualizando foto:', photo);
    setSelectedPhoto(photo);
  };

  // Handler para deletar foto
  const handleDeletePhoto = async () => {
    if (!photoToDelete) return;

    try {
      setIsDeleting(true);
      const response = await apiClient.delete(`/photos/${photoToDelete.id}`);
      
      if (response.data?.success) {
        // Remover a foto da lista
        setRecentPhotos(prev => prev.filter(p => p.id !== photoToDelete.id));
        setPhotoToDelete(null);
        // Recarregar dados do dashboard
        await loadDashboardData();
        await loadDashboardStats();
      } else {
        alert(response.data?.message || 'Erro ao deletar foto');
      }
    } catch (error: any) {
      console.error('Erro ao deletar foto:', error);
      alert(error.response?.data?.message || 'Erro ao deletar foto');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handler para abrir modal de delete
  const handleOpenDeleteModal = (photo: PhotoUpload) => {
    console.log('Abrindo modal de delete para foto:', photo);
    setPhotoToDelete(photo);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Camera className="h-12 w-12 animate-pulse mx-auto mb-4 text-primary" />
          <p className="text-lg">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-blue-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-primary to-teal-600 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Dashboard do Fotografo</h1>
              <p className="text-sm opacity-80">
                Bem-vindo, {getFirstName(user?.name) || user?.email?.split('@')[0] || 'Usuario'}! Gerencie suas fotos e eventos.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setLocation('/photo-upload')}
                className="bg-white text-primary hover:bg-gray-100"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload de Fotos
              </Button>
              <Button
                onClick={() => setLocation('/photographer/events/new')}
                className="bg-white text-primary hover:bg-gray-100 border-2 border-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Evento
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Header Informativo - Dados Bancários */}
      {bankInfo && (bankInfo.bank_name || bankInfo.agency || bankInfo.account_number || bankInfo.pix_key) && (
        <section className="bg-gray-50 border-b border-gray-200 py-3">
          <div className="container mx-auto px-4">
            <div className="flex items-center flex-wrap gap-3 text-sm text-gray-700">
              <CreditCard className="w-5 h-5 text-primary flex-shrink-0" />
              <div className="font-semibold text-gray-800">Dados para Faturamento:</div>
              {bankInfo.bank_name && bankInfo.agency && bankInfo.account_number && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">
                    {bankInfo.bank_name} - Ag: {bankInfo.agency} - Conta: {bankInfo.account_number}
                  </span>
                </div>
              )}
              {bankInfo.pix_key && (
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-800">PIX:</span>
                  <span className="text-gray-600">{bankInfo.pix_key}</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Fotos</CardTitle>
              <Camera className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsData.totalPhotos}</div>
              <p className="text-xs text-muted-foreground">
                Total de fotos enviadas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Fotos Vendidas</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsData.totalSales}</div>
              <p className="text-xs text-muted-foreground">
                Fotos vendidas no total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Receita Total</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R$ {statsData.totalRevenue.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">
                Receita total acumulada
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Vendas Pendentes</CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R$ {statsData.pendingSalesAmount.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">
                {statsData.pendingSalesCount} pedidos aguardando confirmacao
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Gráfico de Vendas Mensais */}
        <div className="mb-8">
          <MonthlySalesChart data={monthlySales} isLoading={loadingMonthlySales} />
        </div>

        {/* Seção Vendas Recentes */}
        <div className="mb-8">
          <RecentSales photographerId={user?.id || ''} />
        </div>

        {/* Tabs de Conteúdo */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="photos">Fotos</TabsTrigger>
            <TabsTrigger value="events">Eventos</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Fotos Recentes */}
              <Card>
                <CardHeader>
                  <CardTitle>Fotos Recentes</CardTitle>
                  <CardDescription>Suas últimas fotos enviadas</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentPhotos.map((photo) => (
                      <div key={photo.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                            {photo.storageurl ? (
                              <img 
                                src={photo.storageurl} 
                                alt={photo.original_filename || photo.filename}
                                className="w-12 h-12 object-cover rounded-lg"
                              />
                            ) : (
                              <Image className="w-6 h-6 text-gray-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{photo.original_filename || photo.filename}</p>
                            <p className="text-xs text-muted-foreground">{photo.eventName || 'Sem evento'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {getStatusBadgeDetailed(photo.status)}
                          <p className="text-xs text-muted-foreground mt-1">
                            {photo.sales || 0} vendas
                          </p>
                          <div className="flex gap-1 mt-2 justify-end">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleViewPhoto(photo)}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-red-600"
                              onClick={() => handleOpenDeleteModal(photo)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Eventos Ativos */}
              <Card>
                <CardHeader>
                  <CardTitle>Eventos Ativos</CardTitle>
                  <CardDescription>Seus eventos em andamento</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {events.slice(0, 3).map((event) => (
                      <div key={event.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{event.name || event.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {event.date ? new Date(event.date).toLocaleDateString('pt-BR') : 'Data nao definida'}
                          </p>
                        </div>
                        <div className="text-right">
                          {getStatusBadgeDetailed(event.status)}
                          <p className="text-xs text-muted-foreground mt-1">
                            {event.photosCount} fotos
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="photos" className="space-y-6">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Minhas Fotos Enviadas</h3>
                <Button
                  onClick={() => setLocation('/photo-upload')}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Novo Upload
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recentPhotos.map((photo) => (
                  <Card key={photo.id} className="overflow-hidden">
                    <div className="aspect-square bg-gray-200 relative">
                      {photo.storageurl ? (
                        <img 
                          src={photo.storageurl} 
                          alt={photo.original_filename || photo.filename}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                          <Image className="w-12 h-12 text-gray-500" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        {getStatusBadgeDetailed(photo.status)}
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h4 className="font-medium mb-2">{photo.original_filename || photo.filename}</h4>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>{photo.eventName || 'Sem evento'}</span>
                        <span>{photo.sales || 0} vendas</span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm font-medium">R$ {(photo.revenue || 0).toFixed(2)}</span>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleViewPhoto(photo)}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600"
                            onClick={() => handleOpenDeleteModal(photo)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {recentPhotos.length === 0 && (
                <div className="text-center py-12">
                  <Image className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg font-medium mb-2">Nenhuma foto enviada</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Comece enviando suas primeiras fotos para os eventos
                  </p>
                  <Button
                    onClick={() => setLocation('/photo-upload')}
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Enviar Fotos
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="events" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Eventos Vinculados</h2>
              <Button onClick={() => setLocation('/create-event')}>
                <Plus className="w-4 h-4 mr-2" />
                Criar Evento
              </Button>
            </div>

            <div className="grid gap-4">
              {events.map((event) => (
                <Card key={event.id} className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold">{event.name}</h3>
                        {getStatusBadgeDetailed(event.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {event.date ? new Date(event.date).toLocaleDateString('pt-BR') : 'Data nao definida'}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Camera className="w-4 h-4" />
                          {event.photosCount || 0} fotos
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      <Button variant="outline" size="sm">
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Seção vazia quando não há eventos */}
            {events.length === 0 && (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nenhum evento vinculado</h3>
                <p className="text-gray-600 mb-4">
                  Crie seu primeiro evento para começar a organizar suas fotos
                </p>
                <Button onClick={() => setLocation('/create-event')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeiro Evento
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de Visualização de Foto */}
      {selectedPhoto && (
        <Dialog open={true} onOpenChange={(open) => {
          if (!open) {
            setSelectedPhoto(null);
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>
                {selectedPhoto.original_filename || selectedPhoto.filename}
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center p-4">
              {selectedPhoto.storageurl ? (
                <img
                  src={selectedPhoto.storageurl}
                  alt={selectedPhoto.original_filename || selectedPhoto.filename}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                  onError={(e) => {
                    console.error('Erro ao carregar imagem:', selectedPhoto);
                    e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Imagem+Indisponivel';
                  }}
                />
              ) : (
                <div className="w-full h-64 bg-gray-200 flex items-center justify-center rounded-lg">
                  <Image className="w-16 h-16 text-gray-400" />
                </div>
              )}
            </div>
            <div className="space-y-2 text-sm">
              <p><strong>Evento:</strong> {selectedPhoto.eventName || 'Sem evento'}</p>
              <p><strong>Status:</strong> {getStatusBadgeDetailed(selectedPhoto.status)}</p>
              <p><strong>Vendas:</strong> {selectedPhoto.sales || 0}</p>
              <p><strong>Receita:</strong> R$ {(selectedPhoto.revenue || 0).toFixed(2)}</p>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal de Confirmação de Delete */}
      {photoToDelete && (
        <AlertDialog open={true} onOpenChange={(open) => {
          if (!open) {
            setPhotoToDelete(null);
          }
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja deletar a foto "{photoToDelete.original_filename || photoToDelete.filename}"?
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                disabled={isDeleting}
                onClick={() => setPhotoToDelete(null)}
              >
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeletePhoto}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? 'Deletando...' : 'Deletar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}